import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { PartialMenu } from "../../../../../interfaces/meMenuInterfaces/interfaces";
import { cloneNameToDisplay } from "../../../utils/cloneNameUtils";
import { SpinnerLoader } from "@services/ui-components";

interface ChangeActiveMenuModalProps {
    show: boolean;
    handleClose: () => void;
    replaceableMenus: PartialMenu[];
    onPublishMenu: (menu: PartialMenu) => void;
    loading?: boolean;
}

const ChangeActiveMenuModal: React.FC<ChangeActiveMenuModalProps> = ({
    show,
    handleClose,
    replaceableMenus,
    onPublishMenu,
    loading,
}) => {
    const { t } = useTranslation();
    const [activeId, setActiveId] = useState<number | null>(null);

    const handleChange = () => {
        const selectedMenu = replaceableMenus.find((m) => m.id === activeId);
        if (!selectedMenu) return;
        onPublishMenu(selectedMenu);
        handleClose();
    };

    useEffect(() => {
        if (!show) return;
        const activeMenu = replaceableMenus.find((m) => m.status?.slug === "activated");
        setActiveId(activeMenu?.id ?? null);
    }, [show, replaceableMenus]);

    return (
        <Modal centered show={show} onHide={handleClose}>
            <Modal.Header closeButton className="border-0 pb-2">
                <Modal.Title className="fs-5">{t("Change the active menu")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                <div className="">
                    <p className="text-grey fs-16 mb-3">
                        {t(
                            "Select the menu to set as the active menu for the establishment. Once published, it will be visible to customers via this QR code and link. If a menu of this type is already published, it will be replaced."
                        )}
                    </p>

                    <div className="overflow-y-auto" style={{ maxHeight: "300px" }}>
                        <ul className="list-unstyled m-0 pt-3">
                            {replaceableMenus.map((menu, index) => {
                                const isActive = activeId === menu.id;
                                const isLast = index === replaceableMenus.length - 1;
                                return (
                                    <React.Fragment key={menu.id}>
                                        <li
                                            onClick={() => setActiveId(menu.id)}
                                            className={`d-flex align-items-center gap-2 ps-3 pe-2 py-2 rounded cursor-pointer list-item ${
                                                isActive ? "text-primary bg-violet-light" : "text-dark"
                                            }`}
                                        >
                                            <i
                                                className={`bi bi-check-lg fs-7 ${isActive ? "visible" : "invisible"}`}
                                            ></i>
                                            <p className="text-wrap mb-0">{cloneNameToDisplay(menu.name, t)}</p>
                                        </li>
                                        {!isLast && <span className="d-block border-bottom my-2"></span>}
                                    </React.Fragment>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between border-top-0 flex-nowrap">
                <Button variant="outline-primary" className="custom-btn" onClick={handleClose}>
                    {t("cancel")}
                </Button>
                <Button variant="primary" className="custom-btn fw-bold" onClick={handleChange} disabled={loading}>
                    {loading ? <SpinnerLoader variant="light" loadingText={t("Loading...")} /> : t("Change")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChangeActiveMenuModal;
